import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { selectUser } from "../../redux/modules/userSlice";
import { AdminAPI } from "../api";
import {
  getCanonicalLocation,
  getHrTeamLocation,
  isUnrestrictedHr,
} from "../../config/locationConfig";
import AdminIcon from "./adminIcons";
import {
  ADMIN_BUTTON_SX,
  ADMIN_COLORS,
  ADMIN_FONT,
  ADMIN_ICON_SIZE,
  ADMIN_SWITCH_SX,
  ADMIN_TYPE,
  AdminPage,
  AdminSheet,
  AdminSheetHeader,
  StatusChip,
  formatDisplayDate,
} from "./adminUi";
import AdminListToolbar from "./AdminListToolbar";
import AdminTable from "./AdminTable";
import DojRangePicker from "./DojRangePicker";
import NewJoineeDialog, { EMPTY_JOINEE_VALUES } from "./NewJoineeDialog";
import JoineeViewDrawer from "./JoineeViewDrawer";
import useJoineeDownloads from "./useJoineeDownloads";
import { employeeRecordPath } from "./adminRoutes";
import {
  JOINEE_FORMS,
  JOINEE_STATUS_META,
  JOINEE_STAT_ORDER,
  countJoineeStats,
  deriveJoineeStatus,
  isInDojRange,
  joineeStatusChipMeta,
  matchesSearch,
  matchesStatTabs,
} from "./joineeStatus";

var TOAST_OPTS = { hideProgressBar: true, closeOnClick: true };


var ACTION_ICON_BUTTON_SX = {
  p: "6px",
  color: ADMIN_COLORS.label,
  "&:hover": { color: ADMIN_COLORS.brand, bgcolor: ADMIN_COLORS.brandSoft },
};

function toDayString(value) {
  if (!value) return "";
  var d = dayjs(value);
  return d.isValid() ? d.format("YYYY-MM-DD") : "";
}

function toListRows(items) {
  return (items || []).map(function (item) {
    var row = {
      id: item._id,
      name: item.name,
      email: item.email,
      designation: item.designation,
      workLocation: item.workLocation,
      doj: item.doj,
      isActive: item.isActive,
      formCompleted: item.formCompleted,
    };
    JOINEE_FORMS.forEach(function (form) {
      row[form.key] = item[form.key];
    });
    row.status = deriveJoineeStatus(row);
    return row;
  });
}

/** Figma stat card; acts as a toggle button for its filter. */
function StatCard(props) {
  var meta = props.meta;
  return (
    <ButtonBase
      onClick={props.onClick}
      aria-pressed={props.active}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: "12px",
        p: "12px",
        minHeight: 80,
        boxSizing: "border-box",
        borderRadius: "6px",
        border: "1px solid " + (props.active ? meta.color : ADMIN_COLORS.lineSoft),
        bgcolor: props.active ? meta.bg : ADMIN_COLORS.card,
        textAlign: "left",
        minWidth: 0,
        transition: "background-color 120ms, border-color 120ms",
        "&:hover": { borderColor: meta.color },
        "&.Mui-focusVisible": { outline: "2px solid " + meta.color, outlineOffset: "1px" },
      }}
    >
      <Box
        sx={{
          width: 52,
          height: 52,
          flexShrink: 0,
          borderRadius: "6px",
          bgcolor: meta.iconColor,
          color: ADMIN_COLORS.card,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <AdminIcon name={meta.icon} size={ADMIN_ICON_SIZE.stat} />
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "center",
        }}
      >
        <Typography
          noWrap
          sx={Object.assign({ fontFamily: ADMIN_FONT, color: ADMIN_COLORS.labelSecondary, maxWidth: "100%" }, ADMIN_TYPE.statLabel)}
        >
          {meta.label}
        </Typography>
        <Typography sx={Object.assign({ fontFamily: ADMIN_FONT, color: ADMIN_COLORS.label }, ADMIN_TYPE.statValue)}>
          {String(props.count).padStart(2, "0")}
        </Typography>
      </Box>
    </ButtonBase>
  );
}

function AdminEmployees() {
  var navigate = useNavigate();
  var { user } = useSelector(selectUser);
  var adminTeamLocation = getHrTeamLocation(user);
  var canChooseWorkLocation = !adminTeamLocation || isUnrestrictedHr(user);
  var downloads = useJoineeDownloads();

  var [rows, setRows] = useState([]);
  var [listLoading, setListLoading] = useState(true);
  var [searchKey, setSearchKey] = useState("");
  var [dojRange, setDojRange] = useState({ start: "", end: "" });
  var [statTabs, setStatTabs] = useState([]);

  var [dialogOpen, setDialogOpen] = useState(false);
  var [dialogMode, setDialogMode] = useState("add");
  var [dialogValues, setDialogValues] = useState(EMPTY_JOINEE_VALUES);
  var [editId, setEditId] = useState("");
  var [originalDoj, setOriginalDoj] = useState("");
  var [originalDojRaw, setOriginalDojRaw] = useState("");

  var [profileId, setProfileId] = useState("");
  var [drawerOpen, setDrawerOpen] = useState(false);
  var [drawerLoading, setDrawerLoading] = useState(false);
  var [newJoineeDetails, setNewJoineeDetails] = useState({});
  var [isActive, setIsActive] = useState(false);

  function loadList() {
    return AdminAPI.getListNewJoinee()
      .then(function (res) {
        setRows(toListRows(res.data));
      })
      .catch(function (err) {
        console.log(err);
      })
      .finally(function () {
        setListLoading(false);
      });
  }

  useEffect(function () {
    loadList();
  }, []);

  var rangedRows = useMemo(
    function () {
      return rows.filter(function (row) {
        return isInDojRange(row, dojRange);
      });
    },
    [rows, dojRange]
  );

  var counts = useMemo(
    function () {
      return countJoineeStats(rangedRows);
    },
    [rangedRows]
  );

  var visibleRows = useMemo(
    function () {
      return rangedRows.filter(function (row) {
        return matchesStatTabs(row, statTabs) && matchesSearch(row, searchKey);
      });
    },
    [rangedRows, statTabs, searchKey]
  );

  var exportRows = useMemo(
    function () {
      return visibleRows.map(function (row, index) {
        return {
          "Sr.No.": index + 1,
          Name: row.name || "",
          Designation: row.designation || "",
          "Date of joining": toDayString(row.doj),
          Email: row.email || "",
          "Work Location": row.workLocation || "",
          Status: JOINEE_STATUS_META[row.status].label,
          Login: row.isActive ? "Active" : "Inactive",
          "Forms Completed (%)": row.formCompleted,
        };
      });
    },
    [visibleRows]
  );

  var hasFilters = Boolean(searchKey.trim() || dojRange.start || statTabs.length);

  /** One status filter at a time; clicking the active card again shows everyone. */
  function toggleStatTab(id) {
    setStatTabs(function (prev) {
      return prev.indexOf(id) === -1 ? [id] : [];
    });
  }

  function clearFilters() {
    setSearchKey("");
    setDojRange({ start: "", end: "" });
    setStatTabs([]);
  }

  function handleActiveInactive(nextActive, id) {
    var status = nextActive ? "active" : "in-active";
    AdminAPI.activeInactiveNewJoinee(id, status)
      .then(function (res) {
        if (res.success) {
          toast.success(res.message, TOAST_OPTS);
          if (id === profileId) setIsActive(nextActive);
          setRows(function (prev) {
            return prev.map(function (row) {
              return row.id === id ? Object.assign({}, row, { isActive: nextActive }) : row;
            });
          });
        } else {
          toast.error(res.message, TOAST_OPTS);
        }
      })
      .catch(function (err) {
        console.log(err);
        toast.error("Unable to update status", TOAST_OPTS);
      });
  }

  function openAddDialog() {
    setEditId("");
    setOriginalDoj("");
    setOriginalDojRaw("");
    setDialogValues(
      Object.assign({}, EMPTY_JOINEE_VALUES, {
        workLocation: !canChooseWorkLocation && adminTeamLocation ? adminTeamLocation : "",
      })
    );
    setDialogMode("add");
    setDialogOpen(true);
  }

  function openEditDialog(id) {
    AdminAPI.getNewJoinee(id)
      .then(function (res) {
        var data = res.data || {};
        var savedWorkLocation = data.workLocation || adminTeamLocation;
        var savedDoj = toDayString(data.doj);
        setEditId(id);
        setOriginalDoj(savedDoj);
        setOriginalDojRaw(data.doj || "");
        setDialogValues({
          companyName: data.companyName || "",
          email: data.email || "",
          name: data.name || "",
          designation: data.designation || "",
          workLocation: getCanonicalLocation(savedWorkLocation) || savedWorkLocation || "",
          doj: savedDoj ? dayjs(savedDoj) : null,
          aadhaar: String(data.aadhaar || "").replace(/\D/g, "").slice(0, 12),
          pan: String(data.pan || "").toUpperCase(),
          phoneNumber: String(data.phoneNumber || "").replace(/\D/g, "").slice(-10),
          recipients: Array.isArray(data.recipients) ? data.recipients : [],
        });
        setDialogMode("edit");
        setDialogOpen(true);
      })
      .catch(function (err) {
        console.log(err);
        toast.error("Unable to load New Joinee details", TOAST_OPTS);
      });
  }

  function closeDialog() {
    setDialogOpen(false);
  }

  function buildPayload(values) {
    var doj = toDayString(values.doj);
    if (dialogMode === "edit" && doj === originalDoj && originalDojRaw) {
      doj = originalDojRaw;
    }
    return {
      companyName: values.companyName,
      email: values.email.trim(),
      name: values.name.trim().replace(/\s+/g, " "),
      designation: values.designation.trim().replace(/\s+/g, " "),
      workLocation: canChooseWorkLocation ? values.workLocation : adminTeamLocation,
      doj: doj,
      aadhaar: values.aadhaar,
      pan: values.pan.toUpperCase(),
      phoneNumber: values.phoneNumber,
      recipients: values.recipients || [],
    };
  }

  function handleDialogSubmit(values) {
    var payload = buildPayload(values);
    var request =
      dialogMode === "edit"
        ? AdminAPI.updateNewJoinee(editId, payload)
        : AdminAPI.createNewJoinee(Object.assign({}, payload, { hrEmail: user && user.email }));

    return request
      .then(function (res) {
        if (res.success) {
          toast.success(res.message, TOAST_OPTS);
          setDialogOpen(false);
          loadList();
          if (dialogMode === "edit" && editId === profileId && drawerOpen) {
            loadDetails(editId);
          }
        } else {
          toast.error(res.message, TOAST_OPTS);
        }
      })
      .catch(function (err) {
        console.log(err);
        var message =
          (err && err.response && err.response.data && err.response.data.message) ||
          "Unable to save New Joinee";
        toast.error(message, TOAST_OPTS);
      });
  }

  function loadDetails(id) {
    setDrawerLoading(true);
    return AdminAPI.getNewJoinee(id)
      .then(function (res) {
        setNewJoineeDetails(res.data || {});
        setIsActive(Boolean(res.data && res.data.isActive));
      })
      .catch(function (err) {
        console.log(err);
        toast.error("Unable to load New Joinee details", TOAST_OPTS);
      })
      .finally(function () {
        setDrawerLoading(false);
      });
  }

  function openDrawer(row) {
    setProfileId(row.id);
    setNewJoineeDetails({});
    setDrawerOpen(true);
    loadDetails(row.id);
  }

  function openRecord() {
    navigate(employeeRecordPath(profileId));
  }

  var columns = [
    {
      field: "sNo",
      header: "Sr.No.",
      width: 76,
      render: function (row, index) {
        return index + 1;
      },
    },
    { field: "name", header: "Name", sortable: true, minWidth: 180, maxWidth: 260 },
    { field: "designation", header: "Designation", muted: true, minWidth: 160, maxWidth: 240 },
    {
      field: "doj",
      header: "Date of joining",
      muted: true,
      width: 160,
      render: function (row) {
        return formatDisplayDate(row.doj);
      },
    },
    { field: "email", header: "Email", muted: true, minWidth: 220, maxWidth: 300 },
    { field: "workLocation", header: "Work Location", muted: true, width: 150 },
    {
      field: "status",
      header: "Status",
      width: 170,
      render: function (row) {
        return <StatusChip size="large" meta={joineeStatusChipMeta(row.status)} />;
      },
    },
    {
      field: "isActive",
      header: "Login",
      width: 90,
      render: function (row) {
        return (
          <Switch
            checked={Boolean(row.isActive)}
            onChange={function (e, checked) {
              handleActiveInactive(checked, row.id);
            }}
            inputProps={{ "aria-label": "Toggle login for " + (row.name || "candidate") }}
            sx={ADMIN_SWITCH_SX}
          />
        );
      },
    },
    {
      field: "actions",
      header: "Actions",
      width: 110,
      render: function (row) {
        return (
          <Box sx={{ display: "flex", gap: "4px" }}>
            <Tooltip title="Edit">
              <IconButton
                aria-label={"Edit " + (row.name || "candidate")}
                onClick={function () {
                  openEditDialog(row.id);
                }}
                sx={ACTION_ICON_BUTTON_SX}
              >
                <AdminIcon name="PencilSimple" size={ADMIN_ICON_SIZE.action} />
              </IconButton>
            </Tooltip>
            <Tooltip title="View">
              <IconButton
                aria-label={"View " + (row.name || "candidate")}
                onClick={function () {
                  openDrawer(row);
                }}
                sx={ACTION_ICON_BUTTON_SX}
              >
                <AdminIcon name="Eye" size={ADMIN_ICON_SIZE.action} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <AdminPage>
      <AdminSheet viewport>
        <AdminSheetHeader
          title="Manage New Joinee"
          action={
            <>
              <DojRangePicker value={dojRange} onChange={setDojRange} />
              <Button
                variant="contained"
                onClick={openAddDialog}
                startIcon={<AdminIcon name="Plus" size={ADMIN_ICON_SIZE.button} />}
                sx={[ADMIN_BUTTON_SX.contained, ADMIN_BUTTON_SX.withStartIcon]}
              >
                Add Candidate
              </Button>
            </>
          }
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)", lg: "repeat(6, 1fr)" },
            gap: "12px",
            px: "20px",
            py: "16px",
            flexShrink: 0,
          }}
          role="group"
          aria-label="Filter by status"
        >
          {JOINEE_STAT_ORDER.map(function (id) {
            return (
              <StatCard
                key={id}
                meta={JOINEE_STATUS_META[id]}
                count={counts[id]}
                active={statTabs.indexOf(id) !== -1}
                onClick={function () {
                  toggleStatTab(id);
                }}
              />
            );
          })}
        </Box>

        <Box sx={{ px: "20px", pb: "20px", display: "flex", flexDirection: "column", gap: "16px", flex: 1, minHeight: 0 }}>
          <AdminListToolbar
            search={searchKey}
            onSearchChange={setSearchKey}
            onClearFilters={hasFilters ? clearFilters : null}
            exportData={exportRows}
            exportFilename="new-joinees.csv"
          />

          <AdminTable
            fill
            columns={columns}
            rows={visibleRows}
            loading={listLoading}
            resetKey={searchKey + "|" + dojRange.start + "|" + dojRange.end + "|" + statTabs.join(",")}
            emptyText={hasFilters ? "No candidates match these filters" : "No candidates yet"}
          />
        </Box>
      </AdminSheet>

      <NewJoineeDialog
        open={dialogOpen}
        mode={dialogMode}
        initialValues={dialogValues}
        originalDoj={originalDoj}
        canChooseWorkLocation={canChooseWorkLocation}
        onClose={closeDialog}
        onSubmit={handleDialogSubmit}
      />

      <JoineeViewDrawer
        open={drawerOpen}
        loading={drawerLoading}
        onClose={function () {
          setDrawerOpen(false);
        }}
        details={newJoineeDetails}
        isActive={isActive}
        onToggleActive={function (checked) {
          handleActiveInactive(checked, profileId);
        }}
        onOpen={openRecord}
        onDownload={function () {
          downloads.download(profileId);
        }}
        downloading={downloads.downloading}
      />

      {downloads.pdfHost}
    </AdminPage>
  );
}

export default AdminEmployees;
